import React from 'react';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import gql from 'graphql-tag';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import Modal from '@reverbdotcom/commons/src/components/modal';
import { useMutation, MutationResult } from '@reverbdotcom/commons/src/useMutation';
import { MutationFn } from 'react-apollo';
import { UpsertMyFavorite, CoreFindFavoriteProviderUpsertMyFavorite } from '../../schemas/rql';
import { withRouter, WithRouterProps } from 'react-router';

interface ExternalProps {
  isOpen: boolean;
  handleClose: Function;
  handleUpdate: Function;
  modalTitle: string;
  searchTitle: string;
  feedEnabled: boolean;
  emailEnabled: boolean;
  queryParams: string;
  handleMutationResult?: (result: MutationResult<UpsertMyFavorite.Mutation>) => void;
}

interface ApolloProps {
  upsertMyFavorite: MutationFn<UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables> | MutationFn<CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>;
}

type IProps = ExternalProps & ApolloProps & WithRouterProps;

export function FavoriteModal(props: IProps) {
  const [upsertFavorite] = useMutation(props.upsertMyFavorite);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await upsertFavorite({
        variables: {
          input: {
            queryParams: props.queryParams,
            feedEnabled: props.feedEnabled,
            emailEnabled: props.emailEnabled,
          },
        },
      });

      if (props.handleMutationResult) {
        props.handleMutationResult(result);
      }

      props.handleClose();
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteSearchesModal' },
        e,
      );
    }
  };

  const handleClose = (e) => {
    if (e) { e.preventDefault(); }
    props.handleClose();
  };

  const renderSubtitle = () => {
    return (
      props.searchTitle &&
      <div className="favorite-modal__header__follow-title">
        {I18n.t('discovery.favorites.confirmationModal.subtitle', { title: props.searchTitle })}
      </div>
    );
  };

  return (
    <Modal
      isOpen={props.isOpen}
      subClass="feed"
      onRequestClose={handleClose}
    >
      <div className="favorite-modal__header">
        <h1>{props.modalTitle}</h1>
        {renderSubtitle()}
      </div>
      <div className="favorite-modal__option">
        <div className="favorite-modal__switch">
          <input
            type="checkbox"
            id="add-to-feed-alert-toggle"
            onChange={(e) => props.handleUpdate('feedEnabled', e.target.checked)}
            checked={props.feedEnabled}
            aria-checked={props.feedEnabled}
          />
          <label htmlFor="add-to-feed-alert-toggle">
            {I18n.t('discovery.favorites.confirmationModal.addToFeed')}
          </label>
        </div>
        {props.feedEnabled &&
          <div className="favorite-modal__switch">
            <input
              type="checkbox"
              id="add-to-email-alert-toggle"
              onChange={e => props.handleUpdate('emailEnabled', e.target.checked)}
              checked={props.emailEnabled}
              aria-checked={props.emailEnabled}
            />
            <label htmlFor="add-to-email-alert-toggle">
              {I18n.t('discovery.favorites.confirmationModal.addToDailyEmail')}
            </label>
          </div>
        }
      </div>
      <div className="favorite-modal__action">
        <button id="submit-follow" type="button" onClick={e => handleSubmit(e)}>
          {I18n.t('discovery.favorites.confirmationModal.submit')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * One mutation and connected component for the favorites hub (modifying settings for existing favorites)
 */
export const FAVORITES_HUB_UPSERT_FAVORITE_MUTATION = gql`
  mutation UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFavoritesHubUpsertFavoriteMutation =
  withGraphql<ExternalProps, UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables>(
    FAVORITES_HUB_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FavoritesHubUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFavoritesHubUpsertFavoriteMutation,
])(FavoriteModal);

/**
 * One mutation and connected component for the FindFavoriteProvider (saving a new favorite from marketplace pages or CSPs)
 */
export const FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION = gql`
  mutation Core_FindFavoriteProvider_UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFindFavoriteProviderUpsertFavoriteMutation =
  withGraphql<ExternalProps, CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>(
    FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FindFavoriteProviderUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFindFavoriteProviderUpsertFavoriteMutation,
])(FavoriteModal);
